import { flow, types } from 'mobx-state-tree';
import { IProject } from '@photosi/expo-photosi-sdk/build/Project';
import { sdkProjects } from '../../_shared/sdkUtils';
import Datadog from '../../../_shared/datadog/index.web';
import { DATADOG_CUSTOM_ACTIONS } from '../../../_shared/datadog/DatadogCustomActions';
import { BaseListenersModel } from './baseListenersModel';
import { delay } from 'src/_shared/utils/delay';

/**
 * Creates a base model (BaseModel) that can be extended by other models in the application.
 * This model provides common functionality and access to shared resources like the Photosi SDK
 * and the application's root store.
 * It even provides a function that verify if the sdkProject is ready or not. The awaitSdkProject function
 * can be used in the afterAttach function of the child models to ensure that the sdkProject is ready before.
 *
 * Purpose of the code:
 * 1. Code reuse: Provides a common base that other models can extend
 * 2. Centralized resource management: Offers unified access to the SDK, root store, and projects
 * 3. Lifecycle management: Takes care of cleaning up resources when the model is removed
 * 4. Photosi integration: Provides a structured interface for interacting with the Photosi SDK
 */
export const BaseModel = BaseListenersModel.named('BaseModel')
  .props({
    projectId: types.string,
    /* isProductUpdating: types.optional(types.boolean, false), */
  })
  .views(self => ({
    get sdkProject(): IProject {
      return sdkProjects.get(self.projectId)!;
    },
  }))
  .actions(self => {
    // To be used in all the afterAttach functions because the sdkProject could be undefined

    const awaitSdkProject = flow(function* awaitSdkProject(attempts: number = 20, timeBetweenAttempts: number = 1000) {
      // the following line is commented because it is not yet implemented on the sdk side. Once it is implemented, we can uncomment it
      // and remove the rest of the code
      // if (!self.sdkProject) {
      //   yield self.sdk.getProjectInstance(projectId);
      // }

      // we must use a for loop because it can't be defined as recursive function because it looses the parent context
      for (let i = 0; i < attempts; i++) {
        if (!!sdkProjects.get(self.projectId)) {
          if (i > 0) {
            // we don't log the first attempt because it is not a retry
            Datadog.logInfo(DATADOG_CUSTOM_ACTIONS.sdk_project_instance_load_time, {
              attempts: i,
              duration: i * (timeBetweenAttempts / 1000),
            });
          }
          // console.log(`sdkProject loaded after ${i} attempts in ${i * (timeBetweenAttempts / 1000)} seconds`);
          return true;
        } else {
          yield delay(timeBetweenAttempts);
        }
      }

      if (!!sdkProjects.get(self.projectId)) {
        return true;
      } else {
        throw new Error(
          `SdkProject is not ready, id: ${self.projectId}, sdkProjects keys: [${Array.from(sdkProjects.keys()).join(', ')}], attempts: ${attempts}, timeBetweenAttempts: ${timeBetweenAttempts}`,
        );
      }
    });

    return { awaitSdkProject };
  });
/* 
 TODO: To be tested to handle isUpdating state from the root instead from product store, once all the stores extends BaseModel
TODO: this way children store can communicate with the root store
 

 .actions(self => ({
    setIsProductUpdating(isProductUpdating: boolean) {
      self.isProductUpdating = isProductUpdating;
      document.dispatchEvent(new CustomEvent('isProductUpdating', { detail: isProductUpdating }));
    },
  })) */
